import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { isFalsyOrWhitespace } from 'vscode/vscode/vs/base/common/strings';
import { joinPath } from 'vscode/vscode/vs/base/common/resources';
import { ExtensionsRegistry } from 'vscode/vscode/vs/workbench/services/extensions/common/extensionsRegistry';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { MenuId, MenuRegistry } from 'vscode/vscode/vs/platform/actions/common/actions';
import { DisposableStore, Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { ThemeIcon } from 'vscode/vscode/vs/base/common/themables';
import { index } from 'vscode/vscode/vs/base/common/arrays';
import { isProposedApiEnabled } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions';
import { Extensions } from 'vscode/vscode/vs/workbench/services/extensionManagement/common/extensionFeatures';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import { platform } from 'vscode/vscode/vs/base/common/process';
import { MarkdownString } from 'vscode/vscode/vs/base/common/htmlContent';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';

const _moduleId = "vs/workbench/services/actions/common/menusExtensionPoint";
const apiMenus = [
    {
        key: 'commandPalette',
        id: MenuId.CommandPalette,
        description: ( localizeWithPath(_moduleId, 0, "The Command Palette")),
        supportsSubmenus: false
    },
    {
        key: 'touchBar',
        id: MenuId.TouchBarContext,
        description: ( localizeWithPath(_moduleId, 1, "The touch bar (macOS only)")),
        supportsSubmenus: false
    },
    {
        key: 'editor/title',
        id: MenuId.EditorTitle,
        description: ( localizeWithPath(_moduleId, 2, "The editor title menu"))
    },
    {
        key: 'editor/title/run',
        id: MenuId.EditorTitleRun,
        description: ( localizeWithPath(_moduleId, 3, "Run submenu inside the editor title menu"))
    },
    {
        key: 'editor/context',
        id: MenuId.EditorContext,
        description: ( localizeWithPath(_moduleId, 4, "The editor context menu"))
    },
    {
        key: 'editor/context/copy',
        id: MenuId.EditorContextCopy,
        description: ( localizeWithPath(_moduleId, 5, "'Copy as' submenu in the editor context menu"))
    },
    {
        key: 'editor/context/share',
        id: MenuId.EditorContextShare,
        description: ( localizeWithPath(_moduleId, 6, "'Share' submenu in the editor context menu")),
        proposed: 'contribShareMenu'
    },
    {
        key: 'explorer/context',
        id: MenuId.ExplorerContext,
        description: ( localizeWithPath(_moduleId, 7, "The file explorer context menu"))
    },
    {
        key: 'explorer/context/share',
        id: MenuId.ExplorerContextShare,
        description: ( localizeWithPath(_moduleId, 8, "'Share' submenu in the file explorer context menu")),
        proposed: 'contribShareMenu'
    },
    {
        key: 'editor/title/context',
        id: MenuId.EditorTitleContext,
        description: ( localizeWithPath(_moduleId, 9, "The editor tabs context menu"))
    },
    {
        key: 'editor/title/context/share',
        id: MenuId.EditorTitleContextShare,
        description: ( localizeWithPath(_moduleId, 10, "'Share' submenu inside the editor title context menu")),
        proposed: 'contribShareMenu'
    },
    {
        key: 'debug/callstack/context',
        id: MenuId.DebugCallStackContext,
        description: ( localizeWithPath(_moduleId, 11, "The debug callstack view context menu"))
    },
    {
        key: 'debug/variables/context',
        id: MenuId.DebugVariablesContext,
        description: ( localizeWithPath(_moduleId, 12, "The debug variables view context menu"))
    },
    {
        key: 'debug/toolBar',
        id: MenuId.DebugToolBar,
        description: ( localizeWithPath(_moduleId, 13, "The debug toolbar menu"))
    },
    {
        key: 'notebook/variables/context',
        id: MenuId.NotebookVariablesContext,
        description: ( localizeWithPath(_moduleId, 14, "The notebook variables view context menu"))
    },
    {
        key: 'menuBar/home',
        id: MenuId.MenubarHomeMenu,
        description: ( localizeWithPath(_moduleId, 15, "The home indicator context menu (web only)")),
        proposed: 'contribMenuBarHome',
        supportsSubmenus: false
    },
    {
        key: 'menuBar/edit/copy',
        id: MenuId.MenubarCopy,
        description: ( localizeWithPath(_moduleId, 16, "'Copy as' submenu in the top level Edit menu"))
    },
    {
        key: 'scm/title',
        id: MenuId.SCMTitle,
        description: ( localizeWithPath(_moduleId, 17, "The Source Control title menu"))
    },
    {
        key: 'scm/sourceControl',
        id: MenuId.SCMSourceControl,
        description: ( localizeWithPath(_moduleId, 18, "The Source Control menu"))
    },
    {
        key: 'scm/sourceControl/title',
        id: MenuId.SCMSourceControlTitle,
        description: ( localizeWithPath(_moduleId, 19, "The Source Control title menu")),
        proposed: 'contribSourceControlTitleMenu'
    },
    {
        key: 'scm/resourceState/context',
        id: MenuId.SCMResourceContext,
        description: ( localizeWithPath(_moduleId, 20, "The Source Control resource state context menu"))
    },
    {
        key: 'scm/resourceFolder/context',
        id: MenuId.SCMResourceFolderContext,
        description: ( localizeWithPath(_moduleId, 21, "The Source Control resource folder context menu"))
    },
    {
        key: 'scm/resourceGroup/context',
        id: MenuId.SCMResourceGroupContext,
        description: ( localizeWithPath(_moduleId, 22, "The Source Control resource group context menu"))
    },
    {
        key: 'scm/change/title',
        id: MenuId.SCMChangeContext,
        description: ( localizeWithPath(_moduleId, 23, "The Source Control inline change menu"))
    },
    {
        key: 'scm/inputBox',
        id: MenuId.SCMInputBox,
        description: ( localizeWithPath(_moduleId, 24, "The Source Control input box menu")),
        proposed: 'contribSourceControlInputBoxMenu'
    },
    {
        key: 'scm/incomingChanges',
        id: MenuId.SCMIncomingChanges,
        description: ( localizeWithPath(_moduleId, 25, "The Source Control incoming changes menu")),
        proposed: 'contribSourceControlHistoryItemGroupMenu'
    },
    {
        key: 'scm/incomingChanges/context',
        id: MenuId.SCMIncomingChangesContext,
        description: ( localizeWithPath(_moduleId, 26, "The Source Control incoming changes context menu")),
        proposed: 'contribSourceControlHistoryItemGroupMenu'
    },
    {
        key: 'scm/outgoingChanges',
        id: MenuId.SCMOutgoingChanges,
        description: ( localizeWithPath(_moduleId, 27, "The Source Control outgoing changes menu")),
        proposed: 'contribSourceControlHistoryItemGroupMenu'
    },
    {
        key: 'scm/outgoingChanges/context',
        id: MenuId.SCMOutgoingChangesContext,
        description: ( localizeWithPath(_moduleId, 28, "The Source Control outgoing changes context menu")),
        proposed: 'contribSourceControlHistoryItemGroupMenu'
    },
    {
        key: 'scm/incomingChanges/allChanges/context',
        id: MenuId.SCMIncomingChangesAllChangesContext,
        description: ( localizeWithPath(_moduleId, 29, "The Source Control all incoming changes context menu")),
        proposed: 'contribSourceControlHistoryItemMenu'
    },
    {
        key: 'scm/incomingChanges/historyItem/context',
        id: MenuId.SCMIncomingChangesHistoryItemContext,
        description: ( localizeWithPath(
            _moduleId,
            30,
            "The Source Control incoming changes history item context menu"
        )),
        proposed: 'contribSourceControlHistoryItemMenu'
    },
    {
        key: 'scm/outgoingChanges/allChanges/context',
        id: MenuId.SCMOutgoingChangesAllChangesContext,
        description: ( localizeWithPath(_moduleId, 31, "The Source Control all outgoing changes context menu")),
        proposed: 'contribSourceControlHistoryItemMenu'
    },
    {
        key: 'scm/outgoingChanges/historyItem/context',
        id: MenuId.SCMOutgoingChangesHistoryItemContext,
        description: ( localizeWithPath(
            _moduleId,
            32,
            "The Source Control outgoing changes history item context menu"
        )),
        proposed: 'contribSourceControlHistoryItemMenu'
    },
    {
        key: 'statusBar/remoteIndicator',
        id: MenuId.StatusBarRemoteIndicatorMenu,
        description: ( localizeWithPath(_moduleId, 33, "The remote indicator menu in the status bar")),
        supportsSubmenus: false
    },
    {
        key: 'terminal/context',
        id: MenuId.TerminalInstanceContext,
        description: ( localizeWithPath(_moduleId, 34, "The terminal context menu"))
    },
    {
        key: 'terminal/title/context',
        id: MenuId.TerminalTabContext,
        description: ( localizeWithPath(_moduleId, 35, "The terminal tabs context menu"))
    },
    {
        key: 'view/title',
        id: MenuId.ViewTitle,
        description: ( localizeWithPath(_moduleId, 36, "The contributed view title menu"))
    },
    {
        key: 'view/item/context',
        id: MenuId.ViewItemContext,
        description: ( localizeWithPath(_moduleId, 37, "The contributed view item context menu"))
    },
    {
        key: 'comments/comment/editorActions',
        id: MenuId.CommentEditorActions,
        description: ( localizeWithPath(_moduleId, 38, "The contributed comment editor actions")),
        proposed: 'contribCommentEditorActionsMenu'
    },
    {
        key: 'comments/commentThread/title',
        id: MenuId.CommentThreadTitle,
        description: ( localizeWithPath(_moduleId, 39, "The contributed comment thread title menu"))
    },
    {
        key: 'comments/commentThread/context',
        id: MenuId.CommentThreadActions,
        description: ( localizeWithPath(
            _moduleId,
            40,
            "The contributed comment thread context menu, rendered as buttons below the comment editor"
        )),
        supportsSubmenus: false
    },
    {
        key: 'comments/commentThread/additionalActions',
        id: MenuId.CommentThreadAdditionalActions,
        description: ( localizeWithPath(
            _moduleId,
            40,
            "The contributed comment thread context menu, rendered as buttons below the comment editor"
        )),
        supportsSubmenus: false,
        proposed: 'contribCommentThreadAdditionalMenu'
    },
    {
        key: 'comments/commentThread/title/context',
        id: MenuId.CommentThreadTitleContext,
        description: ( localizeWithPath(
            _moduleId,
            41,
            "The contributed comment thread title's peek context menu, rendered as a right click menu on the comment thread's peek title."
        )),
        proposed: 'contribCommentPeekContext'
    },
    {
        key: 'comments/comment/title',
        id: MenuId.CommentTitle,
        description: ( localizeWithPath(_moduleId, 42, "The contributed comment title menu"))
    },
    {
        key: 'comments/comment/context',
        id: MenuId.CommentActions,
        description: ( localizeWithPath(
            _moduleId,
            43,
            "The contributed comment context menu, rendered as buttons below the comment editor"
        )),
        supportsSubmenus: false
    },
    {
        key: 'comments/commentThread/comment/context',
        id: MenuId.CommentThreadCommentContext,
        description: ( localizeWithPath(
            _moduleId,
            44,
            "The contributed comment context menu, rendered as a right click menu on the an individual comment in the comment thread's peek view."
        )),
        proposed: 'contribCommentPeekContext'
    },
    {
        key: 'commentsView/commentThread/context',
        id: MenuId.CommentsViewThreadActions,
        description: ( localizeWithPath(
            _moduleId,
            45,
            "The contributed comment thread context menu in the comments view"
        )),
        proposed: 'contribCommentsViewThreadMenus'
    },
    {
        key: 'notebook/toolbar',
        id: MenuId.NotebookToolbar,
        description: ( localizeWithPath(_moduleId, 46, "The contributed notebook toolbar menu"))
    },
    {
        key: 'notebook/kernelSource',
        id: MenuId.NotebookKernelSource,
        description: ( localizeWithPath(_moduleId, 47, "The contributed notebook kernel sources menu")),
        proposed: 'notebookKernelSource'
    },
    {
        key: 'notebook/cell/title',
        id: MenuId.NotebookCellTitle,
        description: ( localizeWithPath(_moduleId, 48, "The contributed notebook cell title menu"))
    },
    {
        key: 'notebook/cell/execute',
        id: MenuId.NotebookCellExecute,
        description: ( localizeWithPath(_moduleId, 49, "The contributed notebook cell execution menu"))
    },
    {
        key: 'interactive/toolbar',
        id: MenuId.InteractiveToolbar,
        description: ( localizeWithPath(_moduleId, 50, "The contributed interactive toolbar menu")),
    },
    {
        key: 'interactive/cell/title',
        id: MenuId.InteractiveCellTitle,
        description: ( localizeWithPath(_moduleId, 51, "The contributed interactive cell title menu")),
    },
    {
        key: 'issue/reporter',
        id: MenuId.IssueReporter,
        description: ( localizeWithPath(_moduleId, 52, "The contributed issue reporter menu")),
        proposed: 'contribIssueReporter'
    },
    {
        key: 'testing/item/context',
        id: MenuId.TestItem,
        description: ( localizeWithPath(_moduleId, 53, "The contributed test item menu")),
    },
    {
        key: 'testing/item/gutter',
        id: MenuId.TestItemGutter,
        description: ( localizeWithPath(_moduleId, 54, "The menu for a gutter decoration for a test item")),
    },
    {
        key: 'testing/item/result',
        id: MenuId.TestPeekElement,
        description: ( localizeWithPath(_moduleId, 55, "The menu for an item in the Test Results view or peek.")),
    },
    {
        key: 'testing/message/context',
        id: MenuId.TestMessageContext,
        description: ( localizeWithPath(
            _moduleId,
            56,
            "A prominent button overlaying editor content where the message is displayed"
        )),
    },
    {
        key: 'testing/message/content',
        id: MenuId.TestMessageContent,
        description: ( localizeWithPath(_moduleId, 57, "Context menu for the message in the results tree")),
    },
    {
        key: 'extension/context',
        id: MenuId.ExtensionContext,
        description: ( localizeWithPath(_moduleId, 58, "The extension context menu"))
    },
    {
        key: 'timeline/title',
        id: MenuId.TimelineTitle,
        description: ( localizeWithPath(_moduleId, 59, "The Timeline view title menu"))
    },
    {
        key: 'timeline/item/context',
        id: MenuId.TimelineItemContext,
        description: ( localizeWithPath(_moduleId, 60, "The Timeline view item context menu"))
    },
    {
        key: 'ports/item/context',
        id: MenuId.TunnelContext,
        description: ( localizeWithPath(_moduleId, 61, "The Ports view item context menu"))
    },
    {
        key: 'ports/item/origin/inline',
        id: MenuId.TunnelOriginInline,
        description: ( localizeWithPath(_moduleId, 62, "The Ports view item origin inline menu"))
    },
    {
        key: 'ports/item/port/inline',
        id: MenuId.TunnelPortInline,
        description: ( localizeWithPath(_moduleId, 63, "The Ports view item port inline menu"))
    },
    {
        key: 'file/newFile',
        id: MenuId.NewFile,
        description: ( localizeWithPath(
            _moduleId,
            64,
            "The 'New File...' quick pick, shown on welcome page and File menu."
        )),
        supportsSubmenus: false,
    },
    {
        key: 'webview/context',
        id: MenuId.WebviewContext,
        description: ( localizeWithPath(_moduleId, 65, "The webview context menu"))
    },
    {
        key: 'file/share',
        id: MenuId.MenubarShare,
        description: ( localizeWithPath(_moduleId, 66, "Share submenu shown in the top level File menu.")),
        proposed: 'contribShareMenu'
    },
    {
        key: 'editor/inlineCompletions/actions',
        id: MenuId.InlineCompletionsActions,
        description: ( localizeWithPath(_moduleId, 67, "The actions shown when hovering on an inline completion")),
        supportsSubmenus: false,
        proposed: 'inlineCompletionsAdditions'
    },
    {
        key: 'editor/inlineEdit/actions',
        id: MenuId.InlineEditActions,
        description: ( localizeWithPath(_moduleId, 68, "The actions shown when hovering on an inline edit")),
        supportsSubmenus: false,
        proposed: 'inlineEdit'
    },
    {
        key: 'editor/content',
        id: MenuId.EditorContent,
        description: ( localizeWithPath(_moduleId, 69, "The prominent button in an editor, overlays its content")),
        proposed: 'contribEditorContentMenu'
    },
    {
        key: 'editor/lineNumber/context',
        id: MenuId.EditorLineNumberContext,
        description: ( localizeWithPath(_moduleId, 70, "The contributed editor line number context menu"))
    },
    {
        key: 'mergeEditor/result/title',
        id: MenuId.MergeInputResultToolbar,
        description: ( localizeWithPath(_moduleId, 71, "The result toolbar of the merge editor")),
        proposed: 'contribMergeEditorMenus'
    },
    {
        key: 'multiDiffEditor/resource/title',
        id: MenuId.MultiDiffEditorFileToolbar,
        description: ( localizeWithPath(_moduleId, 72, "The resource toolbar in the multi diff editor")),
        proposed: 'contribMultiDiffEditorMenus'
    },
    {
        key: 'diffEditor/gutter/hunk',
        id: MenuId.DiffEditorHunkToolbar,
        description: ( localizeWithPath(_moduleId, 73, "The gutter toolbar in the diff editor")),
        proposed: 'contribDiffEditorGutterToolBarMenus'
    },
    {
        key: 'diffEditor/gutter/selection',
        id: MenuId.DiffEditorSelectionToolbar,
        description: ( localizeWithPath(_moduleId, 73, "The gutter toolbar in the diff editor")),
        proposed: 'contribDiffEditorGutterToolBarMenus'
    }
];
var schema;
( ((function(schema) {
    function isMenuItem(item) {
        return typeof item.command === 'string';
    }
    schema.isMenuItem = isMenuItem;
    function isValidMenuItem(item, collector) {
        if (typeof item.command !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                74,
                "property `{0}` is mandatory and must be of type `string`",
                'command'
            )));
            return false;
        }
        if (item.alt && typeof item.alt !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                75,
                "property `{0}` can be omitted or must be of type `string`",
                'alt'
            )));
            return false;
        }
        if (item.when && typeof item.when !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                75,
                "property `{0}` can be omitted or must be of type `string`",
                'when'
            )));
            return false;
        }
        if (item.group && typeof item.group !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                75,
                "property `{0}` can be omitted or must be of type `string`",
                'group'
            )));
            return false;
        }
        return true;
    }
    schema.isValidMenuItem = isValidMenuItem;
    function isValidSubmenuItem(item, collector) {
        if (typeof item.submenu !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                74,
                "property `{0}` is mandatory and must be of type `string`",
                'submenu'
            )));
            return false;
        }
        if (item.when && typeof item.when !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                75,
                "property `{0}` can be omitted or must be of type `string`",
                'when'
            )));
            return false;
        }
        if (item.group && typeof item.group !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                75,
                "property `{0}` can be omitted or must be of type `string`",
                'group'
            )));
            return false;
        }
        return true;
    }
    schema.isValidSubmenuItem = isValidSubmenuItem;
    function isValidItems(items, collector) {
        if (!Array.isArray(items)) {
            collector.error(( localizeWithPath(_moduleId, 76, "submenu items must be an array")));
            return false;
        }
        for (const item of items) {
            if (isMenuItem(item)) {
                if (!isValidMenuItem(item, collector)) {
                    return false;
                }
            }
            else {
                if (!isValidSubmenuItem(item, collector)) {
                    return false;
                }
            }
        }
        return true;
    }
    schema.isValidItems = isValidItems;
    function isValidSubmenu(submenu, collector) {
        if (typeof submenu !== 'object') {
            collector.error(( localizeWithPath(_moduleId, 77, "submenu items must be an object")));
            return false;
        }
        if (typeof submenu.id !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                74,
                "property `{0}` is mandatory and must be of type `string`",
                'id'
            )));
            return false;
        }
        if (typeof submenu.label !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                74,
                "property `{0}` is mandatory and must be of type `string`",
                'label'
            )));
            return false;
        }
        return true;
    }
    schema.isValidSubmenu = isValidSubmenu;
    const menuItem = {
        type: 'object',
        required: ['command'],
        properties: {
            command: {
                description: ( localizeWithPath(
                    _moduleId,
                    78,
                    'Identifier of the command to execute. The command must be declared in the \'commands\'-section'
                )),
                type: 'string'
            },
            alt: {
                description: ( localizeWithPath(
                    _moduleId,
                    79,
                    'Identifier of an alternative command to execute. The command must be declared in the \'commands\'-section'
                )),
                type: 'string'
            },
            when: {
                description: ( localizeWithPath(_moduleId, 80, 'Condition which must be true to show this item')),
                type: 'string'
            },
            group: {
                description: ( localizeWithPath(_moduleId, 81, 'Group into which this item belongs')),
                type: 'string'
            }
        }
    };
    const submenuItem = {
        type: 'object',
        required: ['submenu'],
        properties: {
            submenu: {
                description: ( localizeWithPath(_moduleId, 82, 'Identifier of the submenu to display in this item.')),
                type: 'string'
            },
            when: {
                description: ( localizeWithPath(_moduleId, 80, 'Condition which must be true to show this item')),
                type: 'string'
            },
            group: {
                description: ( localizeWithPath(_moduleId, 81, 'Group into which this item belongs')),
                type: 'string'
            }
        }
    };
    const submenu = {
        type: 'object',
        required: ['id', 'label'],
        properties: {
            id: {
                description: ( localizeWithPath(_moduleId, 83, 'Identifier of the menu to display as a submenu.')),
                type: 'string'
            },
            label: {
                description: ( localizeWithPath(_moduleId, 84, 'The label of the menu item which leads to this submenu.')),
                type: 'string'
            },
            icon: {
                description: ( localizeWithPath(
                    _moduleId,
                    85,
                    '(Optional) Icon which is used to represent the submenu in the UI. Either a file path, an object with file paths for dark and light themes, or a theme icon references, like `\\$(zap)`'
                )),
                anyOf: [{
                        type: 'string'
                    },
                    {
                        type: 'object',
                        properties: {
                            light: {
                                description: ( localizeWithPath(_moduleId, 86, 'Icon path when a light theme is used')),
                                type: 'string'
                            },
                            dark: {
                                description: ( localizeWithPath(_moduleId, 87, 'Icon path when a dark theme is used')),
                                type: 'string'
                            }
                        }
                    }]
            }
        }
    };
    schema.menusContribution = {
        description: ( localizeWithPath(_moduleId, 88, "Contributes menu items to the editor")),
        type: 'object',
        properties: index(apiMenus, menu => menu.key, menu => ({
            markdownDescription: menu.proposed ? ( localizeWithPath(
                _moduleId,
                89,
                "Proposed API, requires `enabledApiProposal: [\"{0}\"]` - {1}",
                menu.proposed,
                menu.description
            )) : menu.description,
            type: 'array',
            items: menu.supportsSubmenus === false ? menuItem : { oneOf: [menuItem, submenuItem] }
        })),
        additionalProperties: {
            description: 'Submenu',
            type: 'array',
            items: { oneOf: [menuItem, submenuItem] }
        }
    };
    schema.submenusContribution = {
        description: ( localizeWithPath(_moduleId, 90, "Contributes submenu items to the editor")),
        type: 'array',
        items: submenu
    };
    function isValidCommand(command, collector) {
        if (!command) {
            collector.error(( localizeWithPath(_moduleId, 91, "expected non-empty value.")));
            return false;
        }
        if (isFalsyOrWhitespace(command.command)) {
            collector.error(( localizeWithPath(
                _moduleId,
                74,
                "property `{0}` is mandatory and must be of type `string`",
                'command'
            )));
            return false;
        }
        if (!isValidLocalizedString(command.title, collector, 'title')) {
            return false;
        }
        if (command.shortTitle && !isValidLocalizedString(command.shortTitle, collector, 'shortTitle')) {
            return false;
        }
        if (command.enablement && typeof command.enablement !== 'string') {
            collector.error(( localizeWithPath(
                _moduleId,
                75,
                "property `{0}` can be omitted or must be of type `string`",
                'precondition'
            )));
            return false;
        }
        if (command.category && !isValidLocalizedString(command.category, collector, 'category')) {
            return false;
        }
        if (!isValidIcon(command.icon, collector)) {
            return false;
        }
        return true;
    }
    schema.isValidCommand = isValidCommand;
    function isValidIcon(icon, collector) {
        if (typeof icon === 'undefined') {
            return true;
        }
        if (typeof icon === 'string') {
            return true;
        }
        else if (typeof icon.dark === 'string' && typeof icon.light === 'string') {
            return true;
        }
        collector.error(( localizeWithPath(
            _moduleId,
            92,
            "property `icon` can be omitted or must be either a string or a literal like `{dark, light}`"
        )));
        return false;
    }
    function isValidLocalizedString(localized, collector, propertyName) {
        if (typeof localized === 'undefined') {
            collector.error(( localizeWithPath(
                _moduleId,
                93,
                "property `{0}` is mandatory and must be of type `string` or `object`",
                propertyName
            )));
            return false;
        }
        else if (typeof localized === 'string' && isFalsyOrWhitespace(localized)) {
            collector.error(( localizeWithPath(
                _moduleId,
                74,
                "property `{0}` is mandatory and must be of type `string`",
                propertyName
            )));
            return false;
        }
        else if (typeof localized !== 'string' && (isFalsyOrWhitespace(localized.original) || isFalsyOrWhitespace(localized.value))) {
            collector.error(( localizeWithPath(
                _moduleId,
                94,
                "properties `{0}` and `{1}` are mandatory and must be of type `string`",
                `${propertyName}.value`,
                `${propertyName}.original`
            )));
            return false;
        }
        return true;
    }
    const commandType = {
        type: 'object',
        required: ['command', 'title'],
        properties: {
            command: {
                description: ( localizeWithPath(_moduleId, 95, 'Identifier of the command to execute')),
                type: 'string'
            },
            title: {
                description: ( localizeWithPath(_moduleId, 96, 'Title by which the command is represented in the UI')),
                type: 'string'
            },
            shortTitle: {
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    97,
                    '(Optional) Short title by which the command is represented in the UI. Menus pick either `title` or `shortTitle` depending on the context in which they show commands.'
                )),
                type: 'string'
            },
            category: {
                description: ( localizeWithPath(
                    _moduleId,
                    98,
                    '(Optional) Category string by which the command is grouped in the UI'
                )),
                type: 'string'
            },
            enablement: {
                description: ( localizeWithPath(
                    _moduleId,
                    99,
                    '(Optional) Condition which must be true to enable the command in the UI (menu and keybindings). Does not prevent executing the command by other means, like the `executeCommand`-api.'
                )),
                type: 'string'
            },
            icon: {
                description: ( localizeWithPath(
                    _moduleId,
                    100,
                    '(Optional) Icon which is used to represent the command in the UI. Either a file path, an object with file paths for dark and light themes, or a theme icon references, like `\\$(zap)`'
                )),
                anyOf: [{
                        type: 'string'
                    },
                    {
                        type: 'object',
                        properties: {
                            light: {
                                description: ( localizeWithPath(_moduleId, 101, 'Icon path when a light theme is used')),
                                type: 'string'
                            },
                            dark: {
                                description: ( localizeWithPath(_moduleId, 102, 'Icon path when a dark theme is used')),
                                type: 'string'
                            }
                        }
                    }]
            }
        }
    };
    schema.commandsContribution = {
        description: ( localizeWithPath(_moduleId, 103, "Contributes commands to the command palette.")),
        oneOf: [
            commandType,
            {
                type: 'array',
                items: commandType
            }
        ]
    };
})(schema || (schema = {}))));
const _commandRegistrations = ( (new DisposableStore()));
const commandsExtensionPoint = ExtensionsRegistry.registerExtensionPoint({
    extensionPoint: 'commands',
    jsonSchema: schema.commandsContribution,
    activationEventsGenerator: (contribs, result) => {
        for (const contrib of contribs) {
            if (contrib.command) {
                result.push(`onCommand:${contrib.command}`);
            }
        }
    }
});
commandsExtensionPoint.setHandler(extensions => {
    function handleCommand(userFriendlyCommand, extension) {
        if (!schema.isValidCommand(userFriendlyCommand, extension.collector)) {
            return;
        }
        const { icon, enablement, category, title, shortTitle, command } = userFriendlyCommand;
        let absoluteIcon;
        if (icon) {
            if (typeof icon === 'string') {
                absoluteIcon = ThemeIcon.fromString(icon) ?? { dark: joinPath(extension.description.extensionLocation, icon), light: joinPath(extension.description.extensionLocation, icon) };
            }
            else {
                absoluteIcon = {
                    dark: joinPath(extension.description.extensionLocation, icon.dark),
                    light: joinPath(extension.description.extensionLocation, icon.light)
                };
            }
        }
        const existingCmd = MenuRegistry.getCommand(command);
        if (existingCmd) {
            if (existingCmd.source) {
                extension.collector.info(( localizeWithPath(
                    _moduleId,
                    104,
                    "Command `{0}` already registered by {1} ({2})",
                    userFriendlyCommand.command,
                    existingCmd.source.title,
                    existingCmd.source.id
                )));
            }
            else {
                extension.collector.info(( localizeWithPath(
                    _moduleId,
                    105,
                    "Command `{0}` already registered",
                    userFriendlyCommand.command
                )));
            }
        }
        _commandRegistrations.add(MenuRegistry.addCommand({
            id: command,
            title,
            source: { id: extension.description.identifier.value, title: extension.description.displayName ?? extension.description.name },
            shortTitle,
            tooltip: title,
            category,
            precondition: ContextKeyExpr.deserialize(enablement),
            icon: absoluteIcon
        }));
    }
    _commandRegistrations.clear();
    for (const extension of extensions) {
        const { value } = extension;
        if (Array.isArray(value)) {
            for (const command of value) {
                handleCommand(command, extension);
            }
        }
        else {
            handleCommand(value, extension);
        }
    }
});
const _submenus = ( (new Map()));
const submenusExtensionPoint = ExtensionsRegistry.registerExtensionPoint({
    extensionPoint: 'submenus',
    jsonSchema: schema.submenusContribution
});
submenusExtensionPoint.setHandler(extensions => {
    _submenus.clear();
    for (const extension of extensions) {
        const { value, collector } = extension;
        for (const [, submenuInfo] of Object.entries(value)) {
            if (!schema.isValidSubmenu(submenuInfo, collector)) {
                continue;
            }
            if (!submenuInfo.id) {
                collector.warn(( localizeWithPath(_moduleId, 106, "`{0}` is not a valid submenu identifier", submenuInfo.id)));
                continue;
            }
            if (( (_submenus.has(submenuInfo.id)))) {
                collector.info(( localizeWithPath(
                    _moduleId,
                    107,
                    "The `{0}` submenu was already previously registered.",
                    submenuInfo.id
                )));
                continue;
            }
            if (!submenuInfo.label) {
                collector.warn(( localizeWithPath(_moduleId, 108, "`{0}` is not a valid submenu label", submenuInfo.label)));
                continue;
            }
            let absoluteIcon;
            if (submenuInfo.icon) {
                if (typeof submenuInfo.icon === 'string') {
                    absoluteIcon = ThemeIcon.fromString(submenuInfo.icon) || { dark: joinPath(extension.description.extensionLocation, submenuInfo.icon) };
                }
                else {
                    absoluteIcon = {
                        dark: joinPath(extension.description.extensionLocation, submenuInfo.icon.dark),
                        light: joinPath(extension.description.extensionLocation, submenuInfo.icon.light)
                    };
                }
            }
            const item = {
                id: MenuId.for(`api:${submenuInfo.id}`),
                label: submenuInfo.label,
                icon: absoluteIcon
            };
            _submenus.set(submenuInfo.id, item);
        }
    }
});
const _apiMenusByKey = ( (new Map( (apiMenus.map(menu => ([menu.key, menu]))))));
const _menuRegistrations = ( (new DisposableStore()));
const _submenuMenuItems = ( (new Map()));
const menusExtensionPoint = ExtensionsRegistry.registerExtensionPoint({
    extensionPoint: 'menus',
    jsonSchema: schema.menusContribution,
    deps: [submenusExtensionPoint]
});
menusExtensionPoint.setHandler(extensions => {
    _menuRegistrations.clear();
    _submenuMenuItems.clear();
    for (const extension of extensions) {
        const { value, collector } = extension;
        for (const entry of Object.entries(value)) {
            if (!schema.isValidItems(entry[1], collector)) {
                continue;
            }
            let menu = _apiMenusByKey.get(entry[0]);
            if (!menu) {
                const submenu = _submenus.get(entry[0]);
                if (submenu) {
                    menu = {
                        key: entry[0],
                        id: submenu.id,
                        description: ''
                    };
                }
            }
            if (!menu) {
                continue;
            }
            if (menu.proposed && !isProposedApiEnabled(extension.description, menu.proposed)) {
                collector.error(( localizeWithPath(
                    _moduleId,
                    109,
                    "{0} is a proposed menu identifier. It requires 'package.json#enabledApiProposals: [\"{1}\"]' and is only available when running out of dev or with the following command line switch: --enable-proposed-api {2}",
                    entry[0],
                    menu.proposed,
                    extension.description.identifier.value
                )));
                continue;
            }
            for (const menuItem of entry[1]) {
                let item;
                if (schema.isMenuItem(menuItem)) {
                    const command = MenuRegistry.getCommand(menuItem.command);
                    const alt = menuItem.alt && MenuRegistry.getCommand(menuItem.alt) || undefined;
                    if (!command) {
                        collector.error(( localizeWithPath(
                            _moduleId,
                            110,
                            "Menu item references a command `{0}` which is not defined in the 'commands' section.",
                            menuItem.command
                        )));
                        continue;
                    }
                    if (menuItem.alt && !alt) {
                        collector.warn(( localizeWithPath(
                            _moduleId,
                            111,
                            "Menu item references an alt-command `{0}` which is not defined in the 'commands' section.",
                            menuItem.alt
                        )));
                    }
                    if (menuItem.command === menuItem.alt) {
                        collector.info(( localizeWithPath(
                            _moduleId,
                            112,
                            "Menu item references the same command as default and alt-command"
                        )));
                    }
                    item = { command, alt, group: undefined, order: undefined, when: undefined };
                }
                else {
                    if (menu.supportsSubmenus === false) {
                        collector.error(( localizeWithPath(
                            _moduleId,
                            113,
                            "Menu item references a submenu for a menu which doesn't have submenu support."
                        )));
                        continue;
                    }
                    const submenu = _submenus.get(menuItem.submenu);
                    if (!submenu) {
                        collector.error(( localizeWithPath(
                            _moduleId,
                            114,
                            "Menu item references a submenu `{0}` which is not defined in the 'submenus' section.",
                            menuItem.submenu
                        )));
                        continue;
                    }
                    let submenuRegistrations = _submenuMenuItems.get(menu.id.id);
                    if (!submenuRegistrations) {
                        submenuRegistrations = ( (new Set()));
                        _submenuMenuItems.set(menu.id.id, submenuRegistrations);
                    }
                    if (( (submenuRegistrations.has(submenu.id.id)))) {
                        collector.warn(( localizeWithPath(
                            _moduleId,
                            115,
                            "The `{0}` submenu was already contributed to the `{1}` menu.",
                            menuItem.submenu,
                            entry[0]
                        )));
                        continue;
                    }
                    submenuRegistrations.add(submenu.id.id);
                    item = { submenu: submenu.id, icon: submenu.icon, title: submenu.label, group: undefined, order: undefined, when: undefined };
                }
                if (menuItem.group) {
                    const idx = menuItem.group.lastIndexOf('@');
                    if (idx > 0) {
                        item.group = menuItem.group.substr(0, idx);
                        item.order = Number(menuItem.group.substr(idx + 1)) || undefined;
                    }
                    else {
                        item.group = menuItem.group;
                    }
                }
                item.when = ContextKeyExpr.deserialize(menuItem.when);
                _menuRegistrations.add(MenuRegistry.appendMenuItem(menu.id, item));
            }
        }
    }
});
let CommandsTableRenderer = class CommandsTableRenderer extends Disposable {
    constructor(_keybindingService) {
        super();
        this._keybindingService = _keybindingService;
        this.type = 'table';
    }
    shouldRender(manifest) {
        return !!manifest.contributes?.commands;
    }
    render(manifest) {
        const rawCommands = manifest.contributes?.commands || [];
        const commands = ( (rawCommands.map(c => ({
            id: c.command,
            title: c.title,
            keybindings: [],
            menus: []
        }))));
        const byId = index(commands, c => c.id);
        const menus = manifest.contributes?.menus || {};
        for (const context in menus) {
            for (const menu of menus[context]) {
                if (menu.command) {
                    let command = byId[menu.command];
                    if (command) {
                        command.menus.push(context);
                    }
                    else {
                        command = { id: menu.command, title: '', keybindings: [], menus: [context] };
                        byId[command.id] = command;
                        commands.push(command);
                    }
                }
            }
        }
        const rawKeybindings = manifest.contributes?.keybindings ? (Array.isArray(manifest.contributes.keybindings) ? manifest.contributes.keybindings : [manifest.contributes.keybindings]) : [];
        rawKeybindings.forEach(rawKeybinding => {
            const keybinding = this.resolveKeybinding(rawKeybinding);
            if (!keybinding) {
                return;
            }
            let command = byId[rawKeybinding.command];
            if (command) {
                command.keybindings.push(keybinding);
            }
            else {
                command = { id: rawKeybinding.command, title: '', keybindings: [keybinding], menus: [] };
                byId[command.id] = command;
                commands.push(command);
            }
        });
        if (!commands.length) {
            return { data: { headers: [], rows: [] }, dispose: () => { } };
        }
        const headers = [
            ( localizeWithPath(_moduleId, 116, "ID")),
            ( localizeWithPath(_moduleId, 117, "Title")),
            ( localizeWithPath(_moduleId, 118, "Keyboard Shortcuts")),
            ( localizeWithPath(_moduleId, 119, "Menu Contexts"))
        ];
        const rows = ( (commands.sort((a, b) => a.id.localeCompare(b.id))
            .map(command => {
            return [
                ( (new MarkdownString())).appendMarkdown(`\`${command.id}\``),
                typeof command.title === 'string' ? command.title : command.title.value,
                command.keybindings,
                ( (new MarkdownString())).appendMarkdown(`${( (command.menus.map(menu => `\`${menu}\``))).join('&nbsp;')}`),
            ];
        })));
        return {
            data: {
                headers,
                rows
            },
            dispose: () => { }
        };
    }
    resolveKeybinding(rawKeyBinding) {
        let key;
        switch (platform) {
            case 'win32':
                key = rawKeyBinding.win;
                break;
            case 'linux':
                key = rawKeyBinding.linux;
                break;
            case 'darwin':
                key = rawKeyBinding.mac;
                break;
        }
        return this._keybindingService.resolveUserBinding(key ?? rawKeyBinding.key)[0];
    }
};
CommandsTableRenderer = ( (__decorate([
    ( (__param(0, IKeybindingService)))
], CommandsTableRenderer)));
( (Registry.as(Extensions.ExtensionFeaturesRegistry))).registerExtensionFeature({
    id: 'commands',
    label: ( localizeWithPath(_moduleId, 120, "Commands")),
    access: {
        canToggle: false,
    },
    renderer: ( (new SyncDescriptor(CommandsTableRenderer))),
});

export { commandsExtensionPoint };
